import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageCircle, X, FileText, Download } from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { signalingService } from '../../firebase/realtimeSignaling';
import toast from 'react-hot-toast';

const LiveClassChat = ({ classId, isOpen, onClose, chatMode, isHost }) => {
  const { currentUser, userProfile } = useRealtime();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const unsubscribeRef = useRef(null);

  useEffect(() => {
    if (!classId || !isOpen) return;

    // Subscribe to chat messages using Realtime Database
    unsubscribeRef.current = signalingService.subscribeToChat(classId, (chatMessages) => {
      setMessages(chatMessages);
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [classId, isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser || sending) return;

    try {
      setSending(true);
      const userName = userProfile?.name || currentUser.displayName || currentUser.email || 'Anonymous';
      await signalingService.sendChatMessage(classId, currentUser.uid, userName, newMessage.trim());
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="w-80 bg-gray-800 border-l border-gray-700 flex flex-col flex-shrink-0">
      <div className="bg-gray-900 p-4 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <MessageCircle size={20} />
          <h3 className="text-white font-semibold">Chat</h3>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-gray-400 text-sm mt-8">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.userId === currentUser?.uid;
            return (
              <div
                key={msg.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[75%] ${isOwn ? 'bg-blue-600' : 'bg-gray-700'} rounded-lg p-2`}>
                  {!isOwn && (
                    <div className="text-xs text-gray-300 mb-1">{msg.userName}</div>
                  )}
                  {msg.messageType === 'pdf' ? (
                    <div className="text-white text-sm">
                      <div className="flex items-center space-x-2">
                        <FileText size={16} />
                        <a
                          href={msg.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 underline flex items-center space-x-1"
                        >
                          <span>{msg.pdfName || 'Download PDF'}</span>
                          <Download size={14} />
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="text-white text-sm">{msg.message}</div>
                  )}
                  {msg.timestamp && (
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(msg.timestamp).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-700">
        <div className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={20} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default LiveClassChat;

